import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  WatchedRun,
  getWatchedRuns,
  removeWatchedRun,
  subscribeWatchedRuns,
} from '@/lib/v3WatchedRuns';
import { ReportProblemButton } from '@/components/support/ReportProblemButton';

interface FinishedRun extends WatchedRun {
  /** needs_browser: the page blocks our servers and waits for a capture from the browser (not final). */
  state: 'complete' | 'failed' | 'needs_browser';
  error?: string | null;
  /** Kept from the status payload so a report from here carries the job's cause. */
  jobId?: string | null;
}

const POLL_MS = 10_000;
const MAX_BANNERS = 3;

export default function V3RunWatcher() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isEnglish = language !== 'el';
  const t = (en: string, el: string) => (isEnglish ? en : el);

  const [runs, setRuns] = useState<WatchedRun[]>(() => getWatchedRuns());
  const [finished, setFinished] = useState<FinishedRun[]>([]);
  const finishedRef = useRef<Set<string>>(new Set());
  // Runs already flagged as waiting for the browser: told once, still watched.
  const nudgedRef = useRef<Set<string>>(new Set());

  useEffect(() => subscribeWatchedRuns(() => setRuns(getWatchedRuns())), []);

  const pending = runs.filter((r) => !finishedRef.current.has(r.shareId));

  const poll = useCallback(async () => {
    const list = getWatchedRuns().filter((r) => !finishedRef.current.has(r.shareId));
    for (const run of list) {
      try {
        const { data, error } = await supabase.functions.invoke('analyze-v3-status', {
          body: { shareId: run.shareId },
        });
        if (error || !data) continue;
        if (data.status === 'complete' || data.status === 'failed') {
          finishedRef.current.add(run.shareId);
          setFinished((prev) =>
            [{ ...run, state: data.status, error: data.error ?? null, jobId: data.jobId ?? null }, ...prev.filter((f) => f.shareId !== run.shareId)].slice(0, MAX_BANNERS),
          );
        } else if (data.status === 'needs_browser' && !nudgedRef.current.has(run.shareId)) {
          // Not finished: the audit waits for one click. Say so once and keep watching.
          nudgedRef.current.add(run.shareId);
          setFinished((prev) =>
            [{ ...run, state: 'needs_browser' as const, error: null, jobId: data.jobId ?? null }, ...prev].slice(0, MAX_BANNERS),
          );
        }
      } catch {
        /* transient network failure: try again on the next tick */
      }
    }
  }, []);

  // Background watching: 10s cadence, fully paused while the tab is hidden.
  useEffect(() => {
    if (pending.length === 0) return;
    let id: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (id) return;
      poll();
      id = setInterval(poll, POLL_MS);
    };
    const stop = () => {
      if (id) clearInterval(id);
      id = null;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [pending.length, poll]);

  const dismiss = (shareId: string) => {
    // A run waiting for the browser stays watched: only its banner goes.
    if (!finishedRef.current.has(shareId) && nudgedRef.current.has(shareId)) {
      setFinished((prev) => prev.filter((f) => f.shareId !== shareId));
      return;
    }
    removeWatchedRun(shareId);
    setFinished((prev) => prev.filter((f) => f.shareId !== shareId));
  };

  const open = (run: FinishedRun) => {
    const target = run.projectId ? `/project/${run.projectId}` : `/free-cro-v3/results/${run.shareId}`;
    if (run.state === 'needs_browser') setFinished((prev) => prev.filter((f) => f.shareId !== run.shareId));
    else dismiss(run.shareId);
    navigate(target);
  };

  // A run whose report is open has been seen: it is done, not waiting.
  const isOpenNow = useCallback((f: WatchedRun) => {
    const path = location.pathname;
    return (!!f.projectId && path.startsWith(`/project/${f.projectId}`)) || path.includes(f.shareId);
  }, [location.pathname]);
  useEffect(() => {
    const seen = finished.filter(isOpenNow);
    if (seen.length === 0) return;
    seen.forEach((f) => { if (f.state !== 'needs_browser') removeWatchedRun(f.shareId); });
    setFinished((prev) => prev.filter((f) => !isOpenNow(f)));
  }, [finished, isOpenNow]);

  const visible = finished.filter((f) => !isOpenNow(f));

  if (visible.length === 0) return null;

  // Under menus and dialogs (z-50), and above the phone CTA bar when it shows.
  return (
    <div className="run-toasts fixed bottom-4 right-4 z-40 flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-3 transition-[bottom] duration-300">
      {visible.map((run) => {
        const failed = run.state === 'failed';
        const waiting = run.state === 'needs_browser';
        return (
          <div
            key={run.shareId}
            className={`relative rounded-[16px] border bg-card p-4 pr-9 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.85)] ${
              failed ? 'border-destructive/40' : 'border-[hsl(var(--ec-mint)/0.4)]'
            }`}
          >
            <button
              type="button"
              onClick={() => dismiss(run.shareId)}
              className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground hover:text-foreground"
              aria-label={t('Dismiss', 'Απόρριψη')}
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-3">
              {failed ? (
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              ) : waiting ? (
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--ec-mint))]" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--ec-mint))]" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {failed
                    ? t('Audit failed', 'Η ανάλυση απέτυχε')
                    : waiting
                      ? 'One click needed'
                      : t('Audit ready', 'Η ανάλυση είναι έτοιμη')}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {waiting
                    ? 'This site blocks automated browsers. Open the audit to capture it from your browser.'
                    : <span className="block truncate">{failed ? run.error || run.url || '' : run.url || ''}</span>}
                </p>
                {waiting ? (
                  <Button size="sm" className="mt-3" onClick={() => open(run)}>
                    Open the audit
                  </Button>
                ) : failed ? (
                  // A banner that says "it failed" and offers nothing is where
                  // most failures go to be forgotten. One click, from here.
                  <div className="mt-3">
                    <ReportProblemButton
                      jobId={run.jobId}
                      errorMessage={run.error}
                      variant="outline"
                    />
                  </div>
                ) : (
                  <Button
                    size="sm"
                    className="mt-3"
                    onClick={() => open(run)}
                  >
                    {t('Open results', 'Άνοιγμα Results')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
